import { DateTime } from "luxon";
import { useMemo } from "react";

import { datesCovered } from "../../lib/dates.js";
import { bandsForRow, spanMeta } from "../../lib/span.js";
import type { Occurrence } from "../../lib/types.js";
import { foregroundOn, initialOf, tintOf } from "../../tokens/palette.js";

/**
 * The kitchen screen's month.
 *
 * A deliberate mirror of the desktop month rather than a new composition —
 * same header arrangement, same grid, same right-hand day panel. If one
 * changes, both do.
 *
 * This view breaks the week view's 28px floor on purpose. The week is read
 * across the room; the month is walked up to and browsed, and scaling its type
 * to 28px would either clip the cells or halve how much of the month fits.
 */

/** Cell content budget, measured: two chips then the overflow line. */
const CHIPS_PER_CELL = 2;
/**
 * And two bands, which cost a chip slot each. Two is what a 131px row holds
 * beside the date; a third would push the chips out of the cell entirely.
 */
const BANDS_PER_ROW = 2;
/** A chip and the gap under it — the band grid keeps the same rhythm. */
const BAND_STEP = 27;
/** The panel is 340px of column; five cards fill it. */
const CARDS_IN_PANEL = 5;

export interface MonthDay {
  date: string;
  occurrences: Occurrence[];
}

export interface MonthPayload {
  generated_at: string;
  timezone: string;
  month: string;
  today: MonthDay;
  days: MonthDay[];
  people: { id: string; display_name: string; color: string }[];
}

function chipColor(occurrence: Occurrence): { color: string; tint: string } {
  // Spec: people[0] drives the colour when several are on an event. The card
  // below still shows a circle for each of them.
  const color = occurrence.people[0]?.color ?? "#1B1A17";
  return { color, tint: tintOf(color) };
}

function metaOf(occurrence: Occurrence, date: string, zone: string): string {
  // Says which day of a run this is, so the panel stops repeating the same
  // sentence for every day of a week away.
  const time = spanMeta(occurrence, date, zone);

  return occurrence.location === null || occurrence.location === ""
    ? time
    : `${time} · ${occurrence.location}`;
}

export function KitchenMonth({
  payload,
  zone,
  selected,
  onSelectDay,
  onOpenEvent,
}: {
  payload: MonthPayload;
  zone: string;
  /** The day the panel is reporting. Null means today. */
  selected: string | null;
  onSelectDay: (date: string) => void;
  onOpenEvent: (occurrenceId: string) => void;
}): React.JSX.Element {
  const month = DateTime.fromISO(`${payload.month}-01`, { zone });
  const todayDate = payload.today.date;

  const panelDay =
    selected === null
      ? payload.today
      : (payload.days.find((d) => d.date === selected) ?? payload.today);

  /**
   * The grid, cut into weeks, with the runs pulled out of the cells.
   *
   * The payload lists an occurrence under every day it covers, which is right
   * for the panel and wrong for the grid: five cells each holding "Svetlana in
   * Denver" is five facts where there is one. A run becomes a band laid across
   * its days instead, and the cells keep their chips for the things that
   * really do happen on one day.
   */
  const weeks = useMemo(() => {
    const rows: { dates: string[]; days: MonthDay[] }[] = [];
    for (let i = 0; i < payload.days.length; i += 7) {
      const slice = payload.days.slice(i, i + 7);
      rows.push({ dates: slice.map((d) => d.date), days: slice });
    }
    return rows;
  }, [payload.days]);

  const spanning = useMemo(() => {
    const seen = new Map<string, Occurrence>();
    for (const day of payload.days) {
      for (const occurrence of day.occurrences) {
        if (occurrence.is_background) continue;
        if (datesCovered(occurrence.starts_at, occurrence.ends_at, zone).length > 1) {
          seen.set(occurrence.occurrence_id, occurrence);
        }
      }
    }
    return [...seen.values()];
  }, [payload.days, zone]);

  const panelDate = DateTime.fromISO(panelDay.date, { zone });
  const panelEvents = panelDay.occurrences;
  const panelShown = panelEvents.slice(0, CARDS_IN_PANEL);
  const panelHidden = panelEvents.length - panelShown.length;

  return (
    <div className="kd-month">
      <div className="kd-month__grid-side">
        <div className="kd-month__dow">
          {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="kd-month__grid">
          {weeks.map((week) => {
            const { bands, lanes } = bandsForRow(spanning, week.dates, zone);
            const shownLanes = Math.min(lanes, BANDS_PER_ROW);

            /*
             * A band pushed past the lane limit is only missing from the days
             * it covers. Counting them row-wide put "+1 more" on a Sunday
             * whose only event was the band drawn straight across it.
             */
            const dropped = bands.filter((band) => band.lane >= BANDS_PER_ROW);
            const hiddenBandsOn = (column: number): number =>
              dropped.filter((band) => column >= band.from && column <= band.to).length;

            return (
              <div key={week.dates[0] ?? ""} className="kd-month__week">
                {week.days.map((day, column) => {
                  const date = DateTime.fromISO(day.date, { zone });
                  const outside = date.month !== month.month || date.year !== month.year;
                  const isToday = day.date === todayDate;
                  const isPanel = day.date === panelDay.date;

                  /*
                   * School and its like are filtered out entirely rather than
                   * shortened. They are context, not plans, and at two chips a
                   * cell a recurring weekday commitment would eat every slot
                   * in the month and hide the things people actually need to
                   * see.
                   */
                  const events = day.occurrences.filter(
                    (o) =>
                      !o.is_background &&
                      datesCovered(o.starts_at, o.ends_at, zone).length === 1,
                  );
                  // The bands overhead have already taken their slots.
                  const room = Math.max(0, CHIPS_PER_CELL - shownLanes);
                  const shown = events.slice(0, room);
                  const hidden = events.length - shown.length + hiddenBandsOn(column);

                  return (
                    <button
                      type="button"
                      key={day.date}
                      className={[
                        "kd-cell",
                        outside ? "is-outside" : "",
                        isToday ? "is-today" : "",
                        isPanel && !isToday ? "is-panel" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-label={date.toFormat("cccc, LLLL d")}
                      onClick={() => onSelectDay(day.date)}
                    >
                      <span className="kd-cell__num">{date.day}</span>

                      <span className="kd-cell__list">
                        {/* Holds the height the bands are using, so the chips
                            below them are not hidden underneath. */}
                        <span
                          className="kd-cell__lanes"
                          style={{ height: shownLanes * BAND_STEP }}
                          aria-hidden="true"
                        />
                        {shown.map((occurrence) => {
                          const { color, tint } = chipColor(occurrence);
                          return (
                            <span
                              key={occurrence.occurrence_id}
                              className="kd-chip"
                              style={{ borderLeftColor: color, background: tint }}
                            >
                              <span className="kd-chip__title">{occurrence.title}</span>
                            </span>
                          );
                        })}
                        {hidden > 0 && <span className="kd-cell__more">+{hidden} more</span>}
                      </span>
                    </button>
                  );
                })}

                <div className="kd-month__bands" aria-hidden="true">
                  {bands
                    .filter((band) => band.lane < BANDS_PER_ROW)
                    .map((band) => {
                      const { color, tint } = chipColor(band.item);
                      return (
                        <span
                          key={band.item.occurrence_id}
                          className={[
                            "kd-chip",
                            "kd-chip--band",
                            band.clippedStart ? "is-clipped-start" : "",
                            band.clippedEnd ? "is-clipped-end" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{
                            gridColumn: `${String(band.from + 1)} / ${String(band.to + 2)}`,
                            gridRow: band.lane + 1,
                            borderLeftColor: color,
                            background: tint,
                          }}
                        >
                          <span className="kd-chip__title">
                            {/* The title is written once, where the run
                                begins. A leading ellipsis says the sentence
                                started in the week above. */}
                            {band.clippedStart ? `… ${band.item.title}` : band.item.title}
                          </span>
                        </span>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="kd-panel">
        <p className="kd-panel__title">{panelDate.toFormat("cccc d")}</p>
        <p className="kd-panel__sub">
          {panelEvents.length === 0
            ? panelDate.toFormat("LLLL")
            : `${String(panelEvents.length)} thing${panelEvents.length === 1 ? "" : "s"} · ${panelDate.toFormat("LLLL")}`}
        </p>

        <div className="kd-panel__list">
          {panelEvents.length === 0 ? (
            <p className="kd-panel__empty">
              {panelDay.date === todayDate
                ? "Nothing on the calendar today. Everyone's home."
                : "Nothing on the calendar."}
            </p>
          ) : (
            <>
              {panelShown.map((occurrence) => {
                const { color, tint } = chipColor(occurrence);
                return (
                  <button
                    type="button"
                    key={occurrence.occurrence_id}
                    className="kd-card"
                    style={{ borderLeftColor: color, background: tint }}
                    onClick={() => onOpenEvent(occurrence.occurrence_id)}
                  >
                    <span className="kd-card__people">
                      {occurrence.people.map((person) => (
                        <span
                          key={person.id}
                          className="disc"
                          style={{
                            width: 22,
                            height: 22,
                            background: person.color,
                            color: foregroundOn(person.color),
                            fontSize: 10,
                          }}
                        >
                          {initialOf(person.display_name)}
                        </span>
                      ))}
                    </span>
                    <span className="kd-card__title">{occurrence.title}</span>
                    <span className="kd-card__meta">
                      {metaOf(occurrence, panelDay.date, zone)}
                    </span>
                  </button>
                );
              })}
              {panelHidden > 0 && (
                <p className="kd-panel__more">
                  +{panelHidden} more{panelDay.date === todayDate ? " today" : ""}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
